import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { UserPlusIcon, ExclamationCircleIcon, CalendarIcon, BuildingOfficeIcon, TagIcon, DocumentTextIcon, MagnifyingGlassIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

const Vendors = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/vendors');
      console.log('Vendors response:', response.data);
      
      if (Array.isArray(response.data)) {
        setVendors(response.data);
      } else {
        console.error('Expected array but got:', typeof response.data, response.data);
        setError('Invalid data format received from server');
        setVendors([]);
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to fetch vendors. Please try again later.');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Filter vendors based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      if (showInactive) {
        setFilteredVendors(vendors);
      } else {
        setFilteredVendors(vendors.filter(vendor => vendor.is_active));
      }
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const baseVendors = showInactive ? vendors : vendors.filter(vendor => vendor.is_active);
      const filtered = baseVendors.filter(vendor => {
        return vendor.name.toLowerCase().includes(lowercasedSearch) ||
               (vendor.dba && vendor.dba.toLowerCase().includes(lowercasedSearch)) ||
               (vendor.vendor_type_name && vendor.vendor_type_name.toLowerCase().includes(lowercasedSearch)) ||
               (vendor.description && vendor.description.toLowerCase().includes(lowercasedSearch));
      });
      setFilteredVendors(filtered);
    }
  }, [searchTerm, vendors, showInactive]);

  const handleRowClick = (vendorId) => {
    navigate(`/vendors/${vendorId}`);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Sort vendors
  const sortedVendors = [...filteredVendors].sort((a, b) => {
    let aValue = a[sortBy] || '';
    let bValue = b[sortBy] || '';
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return (
    <div className="mt-6">
      {/* Header - Indigo Theme */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-500">
        <div className="px-4 py-5 sm:px-6 bg-indigo-50 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-indigo-900">Vendors</h2>
            <p className="mt-1 text-sm text-indigo-700">Manage all vendors in your system.</p>
          </div>
          <Link
            to="/vendors/add"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <UserPlusIcon className="h-5 w-5 mr-2" />
            Add Vendor
          </Link>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mt-4">
        <div className="relative rounded-md shadow-sm max-w-lg">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-indigo-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            name="search"
            id="search"
            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
            placeholder="Search vendors by name, DBA, type, or description"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4 mt-6 border-l-4 border-red-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg border-l-4 border-indigo-100">
          {sortedVendors.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
                <PlusCircleIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No vendors found</h3>
              <p className="mt-2 text-sm text-gray-500">
                Get started by adding a new vendor.
              </p>
              <div className="mt-6">
                <Link
                  to="/vendors/add"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <UserPlusIcon className="h-5 w-5 mr-2" />
                  Add Vendor
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">DBA</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Contact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sortedVendors.map((vendor, index) => (
                    <tr
                      key={vendor.id}
                      className="group cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleRowClick(vendor.id)}
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{vendor.name}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{vendor.dba || '-'}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{vendor.vendor_type_name || '-'}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        {vendor.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Inactive
                          </span>
                        )}
                        {vendor.is_preferred && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            Preferred
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <div className="flex flex-col">
                          {vendor.phone && (
                            <a href={`tel:${vendor.phone}`} className="text-indigo-600 hover:text-indigo-900">
                              {vendor.phone}
                            </a>
                          )}
                          {vendor.website && (
                            <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">
                              Website
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Vendors; 